import { Button, CloseButton, Header, Icon, ImageUploadBase, Screen, Text, TextInput } from '@components'
import {
  ProfileMemoryType,
  useCreateMemory,
  useCreateAlbum,
  useUpdateMemory,
  useUpdateAlbum,
  useDeleteMemory,
  useGetMemoryById,
  useGetAlbumById,
  usePaginateAlbumMemories,
} from '@hooks'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import React, { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { ScreenProfileProps } from 'src/navigation/navigator/ProfileNavigator'
import ContactsListEdit from '../../../menu/components/ContactsListEdit'
import { useLanguage } from '@context'
import { showApiErrorToast, showErrorToast, showSuccessToast } from '@utils'

type ExistingImage = { id: string; uri: string }

export const AddMemory: React.FC<ScreenProfileProps<'ProfileAddMemory'>> = ({
  navigation,
  route,
}): React.ReactElement => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)

  const memoryId = route.params?.memoryId
  const routeAlbumId = route.params?.albumId
  const profileId = route.params?.profileId
  const isEdit = !!memoryId || !!routeAlbumId
  const isAlbumEdit = !!routeAlbumId && !memoryId

  const { mutateAsync: createMemory, isPending: isCreatingMemory } = useCreateMemory()
  const { mutateAsync: createAlbum, isPending: isCreatingAlbum } = useCreateAlbum()
  const { mutateAsync: updateMemory, isPending: isUpdatingMemory } = useUpdateMemory()
  const { mutateAsync: updateAlbum, isPending: isUpdatingAlbum } = useUpdateAlbum()
  const { mutateAsync: deleteMemory, isPending: isDeletingMemory } = useDeleteMemory()
  const { data: existingMemory, isLoading: isLoadingMemory } = useGetMemoryById(memoryId ?? '')
  const { data: albumData, isLoading: isLoadingAlbum } = useGetAlbumById(routeAlbumId ?? '')
  const { flatData: albumMemories } = usePaginateAlbumMemories({ albumId: existingMemory?.albumId ?? '' })
  const { flatData: routeAlbumMemories } = usePaginateAlbumMemories({ albumId: routeAlbumId ?? '' })

  // Add mode state
  const [selectedImages, setSelectedImages] = useState<string[]>(route.params?.preselectedImages ?? [])
  const [albumTitle, setAlbumTitle] = useState('')

  // Edit mode state
  const [existingImages, setExistingImages] = useState<ExistingImage[]>([])
  const originalImages = useRef<ExistingImage[]>([])

  const [newImages, setNewImages] = useState<string[]>([])

  const isPending = isCreatingMemory || isCreatingAlbum || isUpdatingMemory || isUpdatingAlbum || isDeletingMemory

  const { control, handleSubmit, reset } = useForm<ProfileMemoryType>({
    defaultValues: {
      uri: '',
      description: '',
      contactIds: profileId ? [profileId] : [],
    },
  })

  // Pre-populate form when editing a single memory (no album)
  useEffect(() => {
    if (existingMemory && !existingMemory.albumId) {
      reset({
        uri: existingMemory.mediaUrl,
        description: existingMemory.description ?? '',
        contactIds: existingMemory.sharedWithAccountIds,
      })
      const imgs = [{ id: existingMemory.id, uri: existingMemory.mediaUrl }]
      originalImages.current = imgs
      setExistingImages(imgs)
    }
  }, [existingMemory])

  // Pre-populate form when editing an album memory
  useEffect(() => {
    if (existingMemory?.albumId && albumMemories.length > 0) {
      reset({
        uri: existingMemory.mediaUrl,
        description: existingMemory.description ?? '',
        contactIds: existingMemory.sharedWithAccountIds,
      })
      const imgs = albumMemories.map((m) => ({ id: m.id, uri: m.mediaUrl }))
      originalImages.current = imgs
      setExistingImages(imgs)
    }
  }, [albumMemories, existingMemory?.albumId])

  // Pre-populate form when editing an album directly (via albumId route param)
  useEffect(() => {
    if (isAlbumEdit && albumData && routeAlbumMemories.length > 0) {
      reset({
        uri: '',
        description: albumData.description ?? '',
        contactIds: [],
      })
      setAlbumTitle(albumData.title ?? '')
      const imgs = routeAlbumMemories.map((m) => ({ id: m.id, uri: m.mediaUrl }))
      originalImages.current = imgs
      setExistingImages(imgs)
    }
  }, [routeAlbumMemories, albumData, isAlbumEdit])

  const removeExistingImage = (id: string) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== id))
  }

  const removeNewImage = (index: number) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index))
  }

  // Add mode helpers
  const handleImageSelect = (imageUri: string) => {
    setSelectedImages((prev) => [...prev, imageUri])
  }

  const removeSelectedImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index))
  }

  const submit = async (data: ProfileMemoryType) => {
    const sharedWithAccountIds = data.contactIds || []

    if (isEdit) {
      const albumId = existingMemory?.albumId ?? routeAlbumId ?? undefined

      if (!albumId) {
        // Single memory: check if image was replaced (URI changed)
        const current = existingImages[0]
        const original = originalImages.current[0]
        if (current && original && current.uri !== original.uri) {
          // Image replaced: delete old silently, create new with new ID
          await deleteMemory({ id: original.id, silent: true })
          const newId = await createMemory({ uri: current.uri, description: data.description || '', sharedWithAccountIds, silent: true })
          showSuccessToast(t('toast.success'), t('toast.memoryUpdated'))
          // Navigate back to ShowMemory with the new ID (updates params + pops AddMemory)
          navigation.navigate('ProfileShowMemory', { memoryId: newId, readonly: false })
          return
        } else if (current) {
          // Image unchanged: just update metadata
          await updateMemory({ id: current.id, description: data.description || undefined, sharedWithAccountIds })
        }
      } else {
        // Album: 1. Delete removed existing images silently
        const removedIds = originalImages.current
          .filter((o) => !existingImages.find((e) => e.id === o.id))
          .map((o) => o.id)
        await Promise.all(removedIds.map((id) => deleteMemory({ id, silent: true })))

        // 2. Upload new images silently (attach to album)
        await Promise.all(
          newImages.map((uri) =>
            createMemory({ uri, description: data.description || '', sharedWithAccountIds, albumId, silent: true })
          )
        )

        // 3. Update description/contacts on remaining existing memories
        await Promise.all(
          existingImages.map((img) =>
            updateMemory({ id: img.id, description: data.description || undefined, sharedWithAccountIds })
          )
        )

        // 4. Update album metadata
        if (albumId) {
          await updateAlbum({ id: albumId, title: albumTitle.trim() || undefined, description: data.description || undefined })
        }
      }

      showSuccessToast(t('toast.success'), t('toast.memoryUpdated'))
      navigation.goBack()
      return
    }

    // Add mode
    const imagesToUpload = selectedImages.length > 0 ? selectedImages : data.uri ? [data.uri] : []
    if (imagesToUpload.length === 0) {
      // Without this toast, the button is silently a no-op when the user
      // lands on the screen without preselected images and skips the picker.
      showErrorToast(t('errors.title'), t('memoriesForm.selectMemory'))
      return
    }

    try {
      if (imagesToUpload.length > 1) {
        const title = albumTitle.trim() || t('albums.defaultTitle')
        await createAlbum({
          title,
          description: data.description || undefined,
          uris: imagesToUpload,
          sharedWithAccountIds,
        })
      } else {
        await createMemory({
          uri: imagesToUpload[0],
          description: data.description || '',
          sharedWithAccountIds,
        })
      }

      navigation.goBack()
    } catch (err) {
      // The mutation hooks already toast their own translated error; this
      // catch only exists to stop react-hook-form's handleSubmit from
      // swallowing the rejection silently if showApiErrorToast somehow no-ops.
      showApiErrorToast(t, err)
    }
  }

  const isMultiple = selectedImages.length > 1
  const goBack = () => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('ProfileHome')

  return (
    <Screen isPending={isPending || isLoadingMemory || isLoadingAlbum}>
      <Header
        iconLeft="arrowBack"
        iconColor={theme.colors.secondary}
        onPressLeft={goBack}
        title={isEdit ? t('memoriesForm.editMemory') : t('memoriesForm.addMemory')}
      />

      <ScrollView style={styles.container}>
        <View style={{ gap: theme.spacing.xl, padding: theme.spacing.lg }}>

          {/* Image section */}
          <View style={{ gap: theme.spacing.lg }}>
            {isEdit && !existingMemory?.albumId ? (
              // Edit mode: single memory — show image with edit icon to replace
              <ImageUploadBase
                onImageSelect={(image) =>
                  setExistingImages((prev) =>
                    prev.length > 0 ? [{ ...prev[0], uri: image }] : [{ id: memoryId!, uri: image }]
                  )
                }
              >
                <View style={[styles.selectedImageWrapper, styles.singleEditImageWrapper]}>
                  <Image
                    style={styles.selectedImage}
                    source={{ uri: existingImages[0]?.uri }}
                  />
                  <View style={styles.editImageButton}>
                    <Icon name="edit" color={theme.colors.primary} size={11} />
                  </View>
                </View>
              </ImageUploadBase>
            ) : isEdit ? (
              // Edit mode: album — show all images with X + add more
              <View style={styles.selectedImagesContainer}>
                {existingImages.map((img) => (
                  <View key={img.id} style={styles.selectedImageWrapper}>
                    <Image style={styles.selectedImage} source={{ uri: img.uri }} />
                    <TouchableOpacity
                      style={styles.removeImageButton}
                      onPress={() => removeExistingImage(img.id)}
                    >
                      <Icon name="cross" color={theme.colors.white} size={14} />
                    </TouchableOpacity>
                  </View>
                ))}
                {newImages.map((uri, index) => (
                  <View key={`new-${index}`} style={styles.selectedImageWrapper}>
                    <Image style={styles.selectedImage} source={{ uri }} />
                    <TouchableOpacity
                      style={styles.removeImageButton}
                      onPress={() => removeNewImage(index)}
                    >
                      <Icon name="cross" color={theme.colors.white} size={14} />
                    </TouchableOpacity>
                  </View>
                ))}
                <ImageUploadBase onImageSelect={(image) => setNewImages((prev) => [...prev, image])}>
                  <View style={styles.addMoreButton}>
                    <Icon name="plus" color={theme.colors.primary} size={theme.iconSize.medium} />
                  </View>
                </ImageUploadBase>
              </View>
            ) : selectedImages.length > 0 ? (
              // Add mode: images selected
              <View style={styles.selectedImagesContainer}>
                {selectedImages.map((uri, index) => (
                  <View key={index} style={styles.selectedImageWrapper}>
                    <Image style={styles.selectedImage} source={{ uri }} />
                    <TouchableOpacity
                      style={styles.removeImageButton}
                      onPress={() => removeSelectedImage(index)}
                    >
                      <Icon name="cross" color={theme.colors.white} size={14} />
                    </TouchableOpacity>
                  </View>
                ))}
                <ImageUploadBase onImageSelect={(image) => handleImageSelect(image)}>
                  <View style={styles.addMoreButton}>
                    <Icon name="plus" color={theme.colors.primary} size={theme.iconSize.medium} />
                  </View>
                </ImageUploadBase>
              </View>
            ) : (
              // Add mode: no image yet
              <Controller
                control={control}
                name="uri"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <ImageUploadBase
                    onImageSelect={(image) => {
                      onChange(image)
                      handleImageSelect(image)
                    }}
                  >
                    {value ? (
                      <Image style={styles.memoryImage} source={{ uri: value }} />
                    ) : (
                      <View style={styles.memoryImageSelector}>
                        <Icon name="plus" color={theme.colors.primary} size={theme.iconSize.jumbo} />
                        <Text type="bodySmall">{t('memoriesForm.selectMemory')}</Text>
                      </View>
                    )}
                    {error?.message && (
                      <Text style={styles.errorMessage} type="microText" color={theme.colors.error}>
                        {error?.message}
                      </Text>
                    )}
                  </ImageUploadBase>
                )}
              />
            )}
          </View>

          {((isMultiple && !isEdit) || (isEdit && (existingMemory?.albumId || isAlbumEdit))) && (
            <View style={{ gap: theme.spacing.lg }}>
              <Text type="subtitleBold">{t('albums.albumTitle')}</Text>
              <TextInput
                containerStyle={styles.descriptionContainer}
                value={albumTitle}
                onChangeText={setAlbumTitle}
                placeholder={t('albums.defaultTitle')}
                noBorder
              />
            </View>
          )}

          <View style={{ gap: theme.spacing.lg }}>
            <Text type="subtitleBold">{t('memoriesForm.description')}</Text>
            <Controller
              control={control}
              name="description"
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextInput
                  containerStyle={styles.descriptionContainer}
                  style={styles.descriptionInput}
                  value={value}
                  onChangeText={onChange}
                  errorMessage={error?.message}
                  isMultiline
                  noBorder
                />
              )}
            />
          </View>

          <View style={{ gap: theme.spacing.lg }}>
            <Text type="subtitleBold">{t('memoriesForm.identifyContacts')}</Text>
            <Controller
              control={control}
              name="contactIds"
              render={({ field: { value, onChange } }) => (
                <ContactsListEdit
                  title={t('memoriesForm.identifiedContacts')}
                  selectedContactIds={value || []}
                  setSelectedContactIds={onChange}
                  isEdit
                />
              )}
            />
          </View>
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <CloseButton onPress={goBack} />
        <Button
          title={isEdit ? t('common.save') : t('memoriesForm.publish')}
          onPress={handleSubmit(submit)}
          isSmall
        />
      </View>
    </Screen>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    memoryImage: {
      width: '100%',
      height: 200,
      borderRadius: theme.spacing.lg,
    },
    memoryImageSelector: {
      alignItems: 'center',
      backgroundColor: theme.colors.grey10,
      padding: theme.spacing.xl,
      borderRadius: theme.spacing.lg,
    },
    selectedImagesContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.sm,
    },
    selectedImageWrapper: {
      position: 'relative',
    },
    singleEditImageWrapper: {
      alignSelf: 'flex-start'
    },
    selectedImage: {
      width: 120,
      height: 120,
      borderRadius: theme.spacing.sm,
    },
    removeImageButton: {
      position: 'absolute',
      top: -6,
      right: -6,
      backgroundColor: theme.colors.error,
      borderRadius: 10,
      width: 20,
      height: 20,
      justifyContent: 'center',
      alignItems: 'center',
    },
    editImageButton: {
      position: 'absolute',
      top: -6,
      right: -6,
      backgroundColor: theme.colors.grey10,
      borderRadius: 10,
      width: 20,
      height: 20,
      justifyContent: 'center',
      alignItems: 'center',
    },
    addMoreButton: {
      width: 120,
      height: 120,
      borderRadius: theme.spacing.sm,
      backgroundColor: theme.colors.grey10,
      justifyContent: 'center',
      alignItems: 'center',
    },
    errorMessage: {
      alignSelf: 'flex-start',
      paddingVertical: theme.spacing.sm,
      paddingLeft: theme.spacing.lg,
    },
    descriptionContainer: {
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
      borderRadius: theme.spacing.lg,
    },
    descriptionInput: {
      verticalAlign: 'top',
      color: theme.colors.black,
      height: 100,
    },
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.md,
    },
  })
